import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { AddBottle } from './pages/AddBottle.js';
import { ConsultarRotulo } from './pages/ConsultarRotulo.js';
import { NovaGarrafa } from './pages/NovaGarrafa.js';
import { BottleDetail } from './pages/BottleDetail.js';
import { CellarList } from './pages/CellarList.js';
import { CellarSetup } from './pages/CellarSetup.js';
import { CellarStats } from './pages/CellarStats.js';
import { ComposicaoDaAdega } from './pages/ComposicaoDaAdega.js';
import { DrunkBottles } from './pages/DrunkBottles.js';
import { EsperandoVaga } from './pages/EsperandoVaga.js';
import { CellarView } from './pages/CellarView.js';
import { Convites } from './pages/Convites.js';
import { DrinkFirst } from './pages/DrinkFirst.js';
import { EditBottle } from './pages/EditBottle.js';
import { Login } from './pages/Login.js';
import { RedefinirSenha } from './pages/RedefinirSenha.js';
import { Profile } from './pages/Profile.js';
import { WhatToEat } from './pages/WhatToEat.js';
import { AvisoDeConexao } from './components/Conexao.js';
import { ErrorBox, Loading } from './components/Feedback.js';
import { useAuth, useCellars } from './api/queries.js';

const NAV = [
  { to: '/', rotulo: 'Adegas', icone: '🍷' },
  { to: '/beber-primeiro', rotulo: 'Beber antes', icone: '⏳' },
  { to: '/harmonizar', rotulo: 'Harmonizar', icone: '🍽️' },
  // Aponta para a escolha, não para o cadastro: ver `pages/NovaGarrafa.tsx`.
  { to: '/novo', rotulo: 'Adicionar', icone: '＋' },
];

export function App() {
  const { data: auth, isLoading: carregandoSessao, error, refetch } = useAuth();

  if (carregandoSessao) {
    return <Loading texto="Abrindo sua adega…" />;
  }

  /**
   * Falha ao consultar a sessão não é "deslogado": é API ou banco fora do ar.
   * Mostrar a tela de login aqui faria a pessoa achar que a senha é o problema.
   *
   * Mas só quando **ainda não sabemos quem é**. Se a sessão já tinha sido
   * confirmada, uma reconsulta que falha é oscilação de rede, e derrubar o app
   * inteiro por causa dela apaga a tela que a pessoa estava usando. Nesse caso
   * quem avisa é a faixa de conexão, que diz o mesmo sem tirar nada do lugar.
   */
  if (error && !auth) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 px-5">
        <ErrorBox erro={error} />
        <p className="text-center text-sm text-adega-muted">
          Não foi possível falar com o servidor da adega agora.
        </p>
        <button type="button" className="btn-primary" onClick={() => void refetch()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  /*
   * Portão de autenticação: sem sessão, quase só o login existe.
   *
   * A exceção é `/redefinir-senha`, e ela é obrigatória: quem clica no link do
   * e-mail é exatamente quem NÃO consegue entrar. Deixar essa tela do lado de
   * dentro do portão seria pedir a senha para quem veio justamente porque
   * esqueceu a senha.
   *
   * O token vai no fragmento, então ele sobrevive a esta troca de tela sem
   * passar por servidor nenhum.
   */
  if (!auth?.authenticated) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 pb-10">
        <Routes>
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </main>
    );
  }

  return <AppAutenticado />;
}

function AppAutenticado() {
  // A lista continua sendo buscada aqui para o app não entrar com a tela
  // inicial piscando entre "ainda não sei" e o conteúdo. Quem decide o que
  // mostrar com zero adegas é a própria tela inicial, não mais um portão.
  const { isLoading } = useCellars();

  if (isLoading) {
    return <Loading texto="Carregando suas adegas…" />;
  }

  /*
   * Até 2026-10-02 havia um portão aqui: com zero adegas, a ÚNICA tela possível
   * era "Configurar sua adega". Quem não tinha um móvel não entrava.
   *
   * Isso excluía o público maior e mais carente do app: quem tem oito garrafas
   * num armário, quem assina clube de vinho e acumulou sem perceber, quem
   * ganhou garrafas de presente. Essa pessoa não se considera "dona de adega",
   * e o primeiro passo que o app pedia era inventar uma ficção -- prateleiras e
   * capacidade para um armário de cozinha.
   *
   * O dado que denunciou: o primeiro convidado real criou uma adega com as 4
   * prateleiras do padrão (32 vagas), olhou o grid vazio e nunca cadastrou
   * garrafa nenhuma. E o dono do sistema, com 82 garrafas, não conseguia achar
   * UMA pessoa conhecida "com adega" para testar -- porque o filtro estava
   * errado, não a rede dele.
   *
   * O resto do app já aguentava: garrafa sem adega existe no banco, no domínio
   * e no formulário de cadastro. Só faltava a porta de entrada.
   */
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col">
      {/* Sem padding superior: cada tela começa com seu cabeçalho fixo (PageHeader). */}
      <main className="flex-1 px-4 pb-28">
        <Routes>
          {/* Início é a lista de adegas; a adega escolhida vive na URL. */}
          <Route path="/" element={<CellarList />} />
          <Route path="/adega/:cellarId" element={<CellarView />} />
          <Route path="/nova-adega" element={<CellarSetup />} />
          {/* Também do lado de dentro: quem já está logado e clica no link do
              e-mail (trocou de senha por desconfiança, não por estar travado)
              cairia na tela inicial em silêncio, sem entender por quê. */}
          <Route path="/redefinir-senha" element={<RedefinirSenha />} />
          {/* O "+" abre a escolha, não o cadastro direto: as duas ações começam
              fotografando um rótulo, e só depois divergem. */}
          <Route path="/novo" element={<NovaGarrafa />} />
          <Route path="/adicionar" element={<AddBottle />} />
          <Route path="/consultar" element={<ConsultarRotulo />} />
          <Route path="/garrafa/:id" element={<BottleDetail />} />
          <Route path="/garrafa/:id/editar" element={<EditBottle />} />
          <Route path="/beber-primeiro" element={<DrinkFirst />} />
          <Route path="/numeros" element={<CellarStats />} />
          <Route path="/composicao" element={<ComposicaoDaAdega />} />
          <Route path="/ja-bebi" element={<DrunkBottles />} />
          <Route path="/esperando-vaga" element={<EsperandoVaga />} />
          <Route path="/perfil" element={<Profile />} />
          <Route path="/convites" element={<Convites />} />
          <Route path="/harmonizar" element={<WhatToEat />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <AvisoDeConexao />

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-adega-border bg-adega-surface/95 backdrop-blur">
        <div className="mx-auto grid max-w-3xl grid-cols-4 pb-[env(safe-area-inset-bottom)]">
          {NAV.map((item) => {
            // "Adicionar" é a ação principal do app: destaque próprio, ao
            // alcance do polegar.
            const principal = item.to === '/novo';

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `relative flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium transition ${
                    isActive ? 'text-adega-gold' : 'text-adega-muted active:text-adega-text'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && !principal ? (
                      <span
                        aria-hidden
                        className="absolute top-0 h-0.5 w-10 rounded-full bg-adega-gold"
                      />
                    ) : null}
                    {/* Mesma caixa de 36px para todos os ícones: com alturas
                        diferentes, o rótulo de "Adicionar" ficava desalinhado. */}
                    <span
                      aria-hidden
                      className={`grid h-9 w-9 place-items-center leading-none ${
                        principal
                          ? 'rounded-full bg-adega-wine text-lg text-white shadow-lg shadow-adega-wine/30'
                          : 'text-xl'
                      }`}
                    >
                      {item.icone}
                    </span>
                    {item.rotulo}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
